import type { FormEvent } from "react";
import type { PageLink } from "../../api/pages";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ModalShell } from "../../components/ui/modal";
import { useLanguage } from "../../hooks/useLanguage";

export type LinkFormState = {
  label: string;
  url: string;
};

export type LinkModalState = {
  open: boolean;
  editingLink: PageLink | null;
  form: LinkFormState;
  saving: boolean;
  error: string | null;
};

type LinkModalProps = {
  open: boolean;
  loading: boolean;
  error: string | null;
  linkForm: LinkFormState;
  editingLink: PageLink | null;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  onChange: (field: keyof LinkFormState) => (value: string) => void;
};

export function LinkModal({
  open,
  loading,
  error,
  linkForm,
  editingLink,
  onClose,
  onSubmit,
  onChange,
}: LinkModalProps) {
  const { t } = useLanguage();

  const handleLabelChange = (value: string) => {
    onChange("label")(value.slice(0, 120));
  };

  const handleUrlChange = (value: string) => {
    onChange("url")(value.slice(0, 500));
  };

  return (
    <ModalShell open={open} onClose={onClose} disableBackdropClose={loading}>
      <div className="relative w-full rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-semibold mb-4">
          {editingLink ? t.pageBuilder.linkModal.editTitle : t.pageBuilder.linkModal.addTitle}
        </h2>
        <form className="space-y-4" onSubmit={onSubmit}>
          <div className="space-y-2">
            <Label htmlFor="link_url">{t.pageBuilder.linkModal.url.label}</Label>
            <Input
              id="link_url"
              value={linkForm.url}
              onChange={e => handleUrlChange(e.target.value)}
              placeholder={t.pageBuilder.linkModal.url.placeholder}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="link_label">{t.pageBuilder.linkModal.label.label}</Label>
            <Input
              id="link_label"
              value={linkForm.label}
              onChange={e => handleLabelChange(e.target.value)}
              placeholder={t.pageBuilder.linkModal.label.placeholder}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t.pageBuilder.linkModal.cancel}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t.pageBuilder.linkModal.saving : editingLink ? t.pageBuilder.linkModal.save : t.pageBuilder.linkModal.add}
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
}
